import { Injectable } from '@angular/core';
import { Capacitor, CapacitorHttp, registerPlugin, PluginListenerHandle } from '@capacitor/core';
import { StorageService } from './storage.service';

// Define the plugin interface
export interface WatchConnectivityPlugin {
    updateWatchComplication(options: { data: WatchComplicationData }): Promise<{ success: boolean; message: string }>;
    isWatchAppInstalled(): Promise<{ installed: boolean }>;
    isWatchReachable(): Promise<{ reachable: boolean }>;
    sendCredentialsToWatch(options: { userId: string; clubId: string; token: string }): Promise<{ success: boolean; message: string }>;
    addListener(eventName: 'watchRequestedData', listenerFunc: () => void): Promise<PluginListenerHandle>;
}

// Raw API shape for training sessions (matches WatchTrainingData in Swift)
export interface WatchApiTrainingSession {
    date?: string;       // "HH:mm DD/MM/YYYY"
    location?: string;
    group_name?: string;
    club_name?: string;
}

// Raw API shape for race/meet events (matches WatchEventData in Swift)
export interface WatchApiEvent {
    date?: string;
    location?: string;
    name?: string;
    club_name?: string;
}

// Define the data structure for watch complications
export interface WatchComplicationData {
    clubBadgeURL?: string;
    clubName?: string;
    isClubMember?: boolean;
    qrCodeData?: string;
    parkrunBarcode?: string;
    userID?: string;
    clubs?: ClubData[];
    nextTrainingSession?: {
        id: number;
        title: string;
        date: string;
        startTime?: string;
        location?: string;
        groupName?: string;
    };
    // Full session/event lists pushed from iPhone so Bluetooth-only watch users get all content
    trainingSessions?: WatchApiTrainingSession[];
    nextRace?: WatchApiEvent;
    nextMeet?: WatchApiEvent;
}

// Club data for multi-club members
export interface ClubData {
    id: number;
    name: string;
    badgeURL: string;
    isCurrentClub: boolean;
}

// Type for the credentials object
export interface WatchAuthCredentials {
    userId: string;
    clubId: string;
    token: string;
}

// Register the plugin
const WatchConnectivity = registerPlugin<WatchConnectivityPlugin>('WatchConnectivityPlugin', {
    web: () => {
        // Mock implementation for web
        return {
            updateWatchComplication: async () => ({ success: false, message: 'Not available on web' }),
            isWatchAppInstalled: async () => ({ installed: false }),
            sendCredentialsToWatch: async () => ({ success: false, message: 'Not available on web' }),
            isWatchReachable: async () => ({ reachable: false }),
            addListener: async () => ({ remove: async () => { } })
        };
    }
});

@Injectable({
    providedIn: 'root'
})
export class WatchComplicationService {

    private isAvailable = false;
    private listenerHandle?: PluginListenerHandle;

    constructor(private storage: StorageService) {
        // Check if running on iOS
        this.isAvailable = Capacitor.getPlatform() === 'ios';

        if (this.isAvailable) {
            this.setupListener();
        }
    }

    /**
     * Check if Apple Watch app is installed
     */
    async isWatchAppInstalled(): Promise<boolean> {
        if (!this.isAvailable) {
            return false;
        }

        try {
            const result = await WatchConnectivity.isWatchAppInstalled();
            return result.installed;
        } catch (error) {
            console.error('Error checking watch app installation:', error);
            return false;
        }
    }

    /**
     * Check if Apple Watch is currently reachable
     */
    async isWatchReachable(): Promise<boolean> {
        if (!this.isAvailable) {
            return false;
        }

        try {
            const result = await WatchConnectivity.isWatchReachable();
            return result.reachable;
        } catch (error) {
            console.error('Error checking watch reachability:', error);
            return false;
        }
    }

    /**
     * Update watch complication with new data
     */
    async updateComplication(data: WatchComplicationData): Promise<boolean> {
        if (!this.isAvailable) {
            console.log('Watch complications not available on this platform');
            return false;
        }

        try {
            const result = await WatchConnectivity.updateWatchComplication({ data });
            console.log('Watch complication update result:', result);
            return result.success;
        } catch (error) {
            console.error('Error updating watch complication:', error);
            return false;
        }
    }

    /**
     * Send authentication credentials to watch for direct API access
     * This enables the watch to fetch training sessions and other data independently from the server
     */
    async sendCredentials(userId: string, clubId: string, jwtToken: string): Promise<boolean> {
        if (!this.isAvailable) {
            console.log('Watch credentials sync not available on this platform');
            return false;
        }

        try {
            const result = await WatchConnectivity.sendCredentialsToWatch({
                userId,
                clubId,
                token: jwtToken
            });
            console.log('Watch credentials sent:', result);
            return result.success;
        } catch (error) {
            console.error('Error sending credentials to watch:', error);
            return false;
        }
    }

    /**
     * Sync credentials for an already logged-in user
     * Call this when app resumes, on startup, or when watch app is installed
     * 
     * @param getUserCredentials - Function that returns current user credentials
     * @returns Promise<boolean> - true if credentials were sent, false if watch not available or user not logged in
     * 
     * @example
     * ```typescript
     * await this.watchService.syncExistingCredentials(() => ({
     *   userId: this.userService.userDetails.user_id,
     *   clubId: this.userService.clubID.value.toString(),
     *   token: this.userService.userDetails.jwttoken
     * }));
     * ```
     */
    async syncExistingCredentials(
        getUserCredentials: () => WatchAuthCredentials | null
    ): Promise<boolean> {
        if (!this.isAvailable) {
            return false;
        }

        // Check if watch app is installed
        const installed = await this.isWatchAppInstalled();
        if (!installed) {
            console.log('Watch app not installed, skipping credential sync');
            return false;
        }

        // Get current user credentials
        const credentials = getUserCredentials();
        if (!credentials || !credentials.userId || !credentials.token) {
            console.log('No user credentials available, skipping watch sync');
            return false;
        }

        console.log('Syncing credentials with watch for already logged-in user');
        return await this.sendCredentials(
            credentials.userId,
            credentials.clubId,
            credentials.token
        );
    }

    /**
     * Set up listener for when watch requests data
     */
    private async setupListener() {
        try {
            this.listenerHandle = await WatchConnectivity.addListener('watchRequestedData', () => {
                console.log('Watch requested complication data update');
                // You can trigger a refresh here or notify your app to send updated data
                // This could emit an event that other services listen to
            });
        } catch (error) {
            console.error('Error setting up watch listener:', error);
        }
    }

    /**
     * Clean up listener when service is destroyed
     */
    ngOnDestroy() {
        if (this.listenerHandle) {
            this.listenerHandle.remove();
        }
    }

    /**
     * Fetch the full watch data payload from the server and return it ready to push.
     * The URL matches NetworkService.swift so both the watch (WiFi/LTE) and iPhone
     * (Bluetooth push path) call the same endpoint.
     */
    async fetchWatchApiData(userId: string, token: string): Promise<Partial<WatchComplicationData> | null> {
        try {
            const url = `https://app.connectmyclub.co.uk/app/getwatch/apple/${userId}/0/0`;
            const response = await CapacitorHttp.request({
                method: 'GET',
                url,
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.status !== 200 || !response.data?.user) {
                console.warn('[Watch] fetchWatchApiData: empty or error response');
                return null;
            }

            const user = response.data.user;
            return {
                qrCodeData: user.qrcode?.png ?? user.qrcode?.url ?? undefined,
                parkrunBarcode: user.parkrun?.code ?? undefined,
                trainingSessions: user.training ?? [],
                nextRace: user.race ?? undefined,
                nextMeet: user.meet ?? undefined
            };
        } catch (error) {
            console.error('[Watch] fetchWatchApiData failed:', error);
            return null;
        }
    }

    /**
     * Helper method to build complication data from user data
     */
    buildComplicationData(
        clubBadgeURL: string | undefined,
        clubName: string,
        isClubMember: boolean,
        qrCodeData: string | undefined,
        parkrunBarcode: string | undefined,
        userID: string | undefined,
        nextSession: {
            id: number;
            title: string;
            date: string;
            startTime?: string;
            location?: string;
            groupName?: string;
        } | undefined
    ): WatchComplicationData {
        return {
            clubBadgeURL,
            clubName,
            isClubMember,
            qrCodeData,
            parkrunBarcode,
            userID,
            nextTrainingSession: nextSession
        };
    }

    /**
     * Cache user's barcode data for offline use
     * @param userId - User ID
     * @param qrCodeData - QR code data (SVG or data URL)
     * @param parkrunBarcode - Parkrun barcode (base64 encoded SVG)
     * @param parkrunCode - Parkrun athlete code (e.g., "A1647721")
     */
    async cacheBarcodeData(
        userId: string,
        qrCodeData?: string,
        parkrunBarcode?: string,
        parkrunCode?: string
    ): Promise<boolean> {
        try {
            const result = await this.storage.cacheUserBarcodes(userId, qrCodeData, parkrunBarcode, parkrunCode);
            return result.success;
        } catch (error) {
            console.error('Error caching barcode data:', error);
            return false;
        }
    }

    /**
     * Get cached barcode data for offline use
     * @param userId - User ID
     * @returns Cached barcode data or null if not available
     */
    async getCachedBarcodeData(userId: string): Promise<{
        qrCodeData: string | null;
        parkrunBarcode: string | null;
        parkrunCode: string | null;
        cachedAt: number | null;
    } | null> {
        try {
            return await this.storage.getCachedUserBarcodes(userId);
        } catch (error) {
            console.error('Error retrieving cached barcode data:', error);
            return null;
        }
    }

    /**
     * Check if cached barcode data is still valid
     * @param cachedAt - Timestamp when data was cached
     * @param maxAgeMs - Maximum age in milliseconds (default: 7 days)
     */
    isCacheValid(cachedAt: number | null, maxAgeMs?: number): boolean {
        return this.storage.isCacheValid(cachedAt, maxAgeMs);
    }

    /**
     * Update watch complication with cached data if online fetch fails
     * This ensures the watch has data even when offline
     */
    async updateWithCachedDataIfNeeded(
        userId: string,
        clubBadgeURL?: string,
        clubName?: string,
        isClubMember?: boolean,
        nextSession?: {
            id: number;
            title: string;
            date: string;
            startTime?: string;
            location?: string;
            groupName?: string;
        }
    ): Promise<boolean> {
        // Try to get cached barcode data
        const cachedData = await this.getCachedBarcodeData(userId);

        if (!cachedData) {
            console.log('No cached barcode data available');
            return false;
        }

        // Build complication data with cached barcodes
        const complicationData = this.buildComplicationData(
            clubBadgeURL,
            clubName || 'connectMyClub',
            isClubMember || false,
            cachedData.qrCodeData || undefined,
            cachedData.parkrunBarcode || undefined,
            userId,
            nextSession
        );

        // Update watch with cached data
        return await this.updateComplication(complicationData);
    }
}
